// Hero ability bar with cooldown tracking and ability picker popup.
// Shows 5 ability slots — empty slots can be filled via the picker when
// the hero has unused ability picks. Active abilities show a "Use" button
// unless on cooldown or the hero is wounded/captured.
import { Button } from '@aod/ui/components/ui/button';
import { SceneImage } from '@aod/ui/components/ui/scene';
import { useMemo, useState } from 'react';
import { useT } from '../../i18n/index.js';
import { plural } from '../../i18n/rich.js';
import { isSceneId, sceneUrls } from '../scenes.js';
import { getAbilitiesForRole, getAbilityInfo } from './heroAbilities.js';
import type { AbilitySlot } from './types.js';

interface HeroAbilityBarProps {
  readonly unitId: string;
  readonly abilitySlots: readonly AbilitySlot[];
  readonly role: string;
  readonly level: number;
  readonly isWounded: boolean;
  readonly unusedPicks: number;
  readonly onActivateAbility: (unitId: string, slotIndex: number) => void;
  readonly onSelectAbility: (unitId: string, slotIndex: number, abilityId: string) => void;
}

export function HeroAbilityBar({
  unitId,
  abilitySlots,
  role,
  level,
  isWounded,
  unusedPicks,
  onActivateAbility,
  onSelectAbility,
}: HeroAbilityBarProps) {
  const t = useT();
  const [showAbilityPicker, setShowAbilityPicker] = useState(false);

  const availableAbilities = useMemo(() => getAbilitiesForRole(role, level), [role, level]);

  return (
    <div>
      <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
        {t('hero.abilities')}
      </span>
      <div className="mt-1 space-y-1">
        {abilitySlots.map((slot, i) => {
          const info = slot.abilityId ? getAbilityInfo(slot.abilityId) : undefined;
          const onCooldown = slot.cooldown > 0;
          return (
            <div
              key={slot.abilityId ?? `empty-${i}`}
              className={`flex items-center justify-between rounded px-2 py-1 text-[11px] ${
                info ? (onCooldown || isWounded ? 'bg-muted/20' : 'bg-muted/40') : 'bg-muted/10'
              }`}
            >
              {slot.abilityId && isSceneId('heroAbility', slot.abilityId) && (
                <SceneImage
                  {...sceneUrls('heroAbility', slot.abilityId)}
                  widths={[64, 128]}
                  sizes="28px"
                  aspect="square"
                  rounded="md"
                  scrim={false}
                  className={`mr-2 size-7 shrink-0 ${onCooldown || isWounded ? 'opacity-50' : ''}`}
                  data-testid={`hero-ability-icon-${slot.abilityId}`}
                />
              )}
              <div className="flex min-w-0 flex-1 flex-col">
                <span
                  className={`truncate ${info ? 'text-foreground' : 'text-muted-foreground/50'}`}
                >
                  {info ? info.name : t('hero.emptySlot', { slot: i + 1 })}
                </span>
                {info && (
                  <span className="text-[9px] text-muted-foreground">
                    {onCooldown ? t('hero.cooldown', { ticks: slot.cooldown }) : info.description}
                  </span>
                )}
              </div>
              {info && !onCooldown && !isWounded && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => onActivateAbility(unitId, i)}
                >
                  {t('hero.use')}
                </Button>
              )}
              {onCooldown && (
                <span className="text-[10px] font-mono text-muted-foreground">
                  {slot.cooldown}t
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Unused Ability Picks */}
      {unusedPicks > 0 && (
        <div className="mt-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full text-[11px]"
            onClick={() => setShowAbilityPicker(!showAbilityPicker)}
          >
            {showAbilityPicker
              ? t('common.cancel')
              : t(plural('hero.picks', unusedPicks), { count: unusedPicks })}
          </Button>
          {showAbilityPicker && (
            <div className="mt-1 max-h-32 space-y-0.5 overflow-y-auto rounded border border-border/50 p-1">
              {availableAbilities.length === 0 ? (
                <span className="text-[10px] text-muted-foreground px-1">
                  {t('hero.noAbilities')}
                </span>
              ) : (
                availableAbilities.map((a) => {
                  const alreadyKnown = abilitySlots.some((s) => s.abilityId === a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      disabled={alreadyKnown}
                      className={`w-full rounded px-2 py-1 text-left text-[10px] transition-colors ${
                        alreadyKnown
                          ? 'cursor-not-allowed bg-muted/10 text-muted-foreground/50'
                          : 'bg-muted/30 hover:bg-muted/50'
                      }`}
                      onClick={() => {
                        const emptySlot = abilitySlots.findIndex((s) => !s.abilityId);
                        const slotIndex = emptySlot >= 0 ? emptySlot : 0;
                        onSelectAbility(unitId, slotIndex, a.id);
                        setShowAbilityPicker(false);
                      }}
                    >
                      <span className="font-medium">{a.name}</span>
                      <span className="ml-1 text-muted-foreground">— {a.description}</span>
                      {alreadyKnown && (
                        <span className="ml-1 text-amber-700 dark:text-amber-400">
                          {t('hero.known')}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
